'use client'

import { useEffect, useState } from 'react'
import { Download, ArrowUpRight, Smartphone, Terminal, Usb } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { stats } from '@/lib/chirp-data'

function GitHubIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M12 .3a12 12 0 0 0-3.8 23.4c.6.1.8-.3.8-.6v-2.3c-3.3.7-4-1.6-4-1.6-.5-1.4-1.3-1.7-1.3-1.7-1.1-.8.1-.8.1-.8 1.2.1 1.8 1.2 1.8 1.2 1.1 1.8 2.8 1.3 3.5 1 .1-.8.4-1.3.8-1.6-2.7-.3-5.5-1.3-5.5-5.9 0-1.3.5-2.4 1.2-3.2-.1-.3-.5-1.5.1-3.2 0 0 1-.3 3.3 1.2a11.5 11.5 0 0 1 6 0C17.3 5 18.3 5.3 18.3 5.3c.6 1.7.2 2.9.1 3.2.8.8 1.2 1.9 1.2 3.2 0 4.6-2.8 5.6-5.5 5.9.4.4.8 1.1.8 2.2v3.3c0 .3.2.7.8.6A12 12 0 0 0 12 .3Z" />
    </svg>
  )
}

type HeroSlide = {
  src: string
  alt: string
  caption: string
  credit?: {
    author: string
    authorHref: string
    license: string
    licenseHref: string
  }
}

const slides: HeroSlide[] = [
  {
    src: '/operators/field-day.png',
    alt: 'Ham radio operators enjoying a field day event outdoors',
    caption: 'Field Day — operating portable with friends',
  },
  {
    src: '/operators/home-shack.png',
    alt: 'Operator smiling in a glowing home radio shack at night',
    caption: 'The home shack — chasing contacts after dark',
  },
  {
    src: '/operators/mountain-top.png',
    alt: 'Two operators activating a summit with a handheld radio',
    caption: 'Summits On The Air — radio in the wild',
  },
  {
    src: '/operators/jeepalooza.jpg',
    alt: 'A large group of Jeep enthusiasts gathered with their vehicles',
    caption: 'Jeep owners gathering — the kind of crew GMRS keeps connected',
    credit: {
      author: 'Photo: Jeepalooza',
      authorHref: 'https://www.flickr.com/photos/135539315@N07/21116125142',
      license: 'CC BY 2.0',
      licenseHref: 'https://creativecommons.org/licenses/by/2.0/',
    },
  },
]

export function Hero() {
  const [active, setActive] = useState(0)

  useEffect(() => {
    const id = setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length)
    }, 4500)
    return () => clearInterval(id)
  }, [])

  return (
    <section id="top" className="relative overflow-hidden border-b border-border/60">
      <div className="grid-bg absolute inset-0" aria-hidden />
      <div
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -left-40 top-1/2 h-96 w-96 -translate-y-1/2 rounded-full bg-primary/15 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-40 top-20 h-96 w-96 rounded-full bg-accent/10 blur-3xl"
        aria-hidden
      />

      <div className="relative mx-auto max-w-7xl px-4 pt-12 pb-6 md:pt-16 md:pb-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          {/* Copy */}
          <div className="text-center lg:text-left">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 font-mono text-xs text-muted-foreground">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
              </span>
              The next generation of CHIRP · Android beta available
            </span>

            <h1 className="mt-6 text-balance text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
              Program your radio at your desk or{' '}
              <span className="text-primary">in the field</span>
            </h1>

            <p className="mx-auto mt-6 max-w-xl text-pretty text-lg leading-relaxed text-muted-foreground lg:mx-0">
              NewChirp brings modern radio programming to Windows, macOS, Linux, and now Android.
              Manage hundreds of amateur, GMRS, and commercial radio models from your computer — or
              read, edit, and write channels directly from your phone over Bluetooth LE or USB OTG.
            </p>

            <a
              href="https://github.com/ricktek954/newchirp"
              target="_blank"
              rel="noreferrer"
              aria-label="View NewChirp source code on GitHub (opens in a new tab)"
              className="github-blink mt-5 inline-flex items-center gap-2 rounded-xl border border-border bg-card/90 px-4 py-2.5 text-sm font-semibold shadow-sm transition-colors hover:border-primary/40 hover:bg-secondary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <GitHubIcon className="h-5 w-5" />
              View source on GitHub
              <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
            </a>

            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row lg:justify-start">
              <Button size="lg" nativeButton={false} render={<a href="#download" />}>
                <Download className="h-4 w-4" />
                Download NewChirp
              </Button>
              <Button
                className="web-programmer-halo"
                size="lg"
                variant="outline"
                nativeButton={false}
                render={<a href="/programmer" />}
              >
                <Usb className="h-4 w-4" />
                Try Web Programmer
              </Button>
              <Button
                size="lg"
                variant="outline"
                nativeButton={false}
                render={<a href="/beginners-guide#mobile" />}
              >
                <Smartphone className="h-4 w-4" />
                Mobile guide
              </Button>
            </div>

            <p className="mt-4 flex items-center justify-center gap-2 font-mono text-xs text-muted-foreground lg:justify-start">
              <Terminal className="h-3.5 w-3.5" />
              Windows · macOS · Linux · Android 8+
            </p>
          </div>

          {/* Rotating image showcase */}
          <div className="relative">
            <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-border bg-card shadow-2xl shadow-primary/10">
              {slides.map((slide, i) => (
                <div
                  key={slide.src}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    i === active ? 'opacity-100' : 'opacity-0'
                  }`}
                  aria-hidden={i !== active}
                >
                  <img
                    src={slide.src}
                    alt={slide.alt}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/10 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-16">
                    <p className="text-pretty text-sm font-medium text-foreground drop-shadow">
                      {slide.caption}
                    </p>
                    {slide.credit && (
                      <p className="mt-1 text-[10px] text-muted-foreground">
                        <a
                          href={slide.credit.authorHref}
                          target="_blank"
                          rel="noreferrer"
                          className="underline underline-offset-2 hover:text-foreground"
                        >
                          {slide.credit.author}
                        </a>
                        {' · '}
                        <a
                          href={slide.credit.licenseHref}
                          target="_blank"
                          rel="noreferrer"
                          className="underline underline-offset-2 hover:text-foreground"
                        >
                          {slide.credit.license}
                        </a>
                      </p>
                    )}
                  </div>
                </div>
              ))}

              {/* Slide indicators */}
              <div className="absolute bottom-4 right-4 flex gap-1.5">
                {slides.map((slide, i) => (
                  <button
                    key={slide.src}
                    type="button"
                    onClick={() => setActive(i)}
                    aria-label={`Show slide ${i + 1}`}
                    aria-current={i === active}
                    className={`h-2 rounded-full transition-all ${
                      i === active ? 'w-6 bg-primary' : 'w-2 bg-foreground/40 hover:bg-foreground/70'
                    }`}
                  />
                ))}
              </div>
            </div>
            <span className="absolute -bottom-3 -right-3 -z-10 hidden h-full w-full rounded-2xl border border-primary/30 sm:block" />
          </div>
        </div>

        <dl className="mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border md:grid-cols-4">
          {stats.map((stat) => (
            <div key={stat.label} className="bg-card px-4 py-6 text-center">
              <dt className="sr-only">{stat.label}</dt>
              <dd className="font-mono text-3xl font-bold text-primary">{stat.value}</dd>
              <dd className="mt-1 text-sm text-muted-foreground">{stat.label}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  )
}
